<template>
  <div class="home-page">
    <!-- 通栏绿色渐变 Hero：section 撑满视口宽，内容由 page-container 限宽 -->
    <section class="hero">
      <div class="page-container">
        <span class="hero-badge"><i class="el-icon-magic-stick" />常用小工具集合</span>
        <h1>快捷工具箱</h1>
        <p>集合常用小工具，提升工作效率</p>
        <div class="search-box">
          <i class="el-icon-search" />
          <input v-model="keyword" type="text" placeholder="搜索工具..." />
        </div>
      </div>
    </section>

    <section class="page-container">
      <div class="section-header">
        <h2>工具列表</h2>
        <p>常用工具一键直达</p>
      </div>
      <div class="tool-grid">
        <div v-for="tool in filteredTools" :key="tool.path" class="tool-card" @click="goTo(tool)">
          <span class="tool-icon"><i :class="tool.icon" /></span>
          <div class="tool-info">
            <h3>{{ tool.name }}</h3>
            <p>{{ tool.desc }}</p>
          </div>
          <i class="el-icon-arrow-right tool-arrow" />
        </div>
        <div class="tool-card more" @click="$message.info('更多工具敬请期待')">
          <span class="tool-icon more-icon"><i class="el-icon-more-outline" /></span>
          <div class="tool-info">
            <h3>更多工具</h3>
            <p>敬请期待...</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { authBus } from '../store/auth'

export default {
  name: 'HomePage',
  data() {
    return {
      keyword: '',
      tools: [
        { name: '日历查询', desc: '查询工作日、双休和节假日', path: '/calendar', icon: 'el-icon-date' },
        { name: '日历接入', desc: '复制接口信息，让 AI 帮你填写日期', path: '/calendar-access', icon: 'el-icon-connection' },
        { name: '文件合并', desc: '按指定顺序合并多个文本文件', path: '/file-merge', icon: 'el-icon-document-copy' },
        { name: '密码生成器', desc: '使用 Argon2id 为任意主体派生强密码', path: '/password-generator', icon: 'el-icon-lock', requiresAuth: true },
        { name: '键盘清洁', desc: '全屏锁定页面，方便安全擦拭键盘', path: '/keyboard-clean', icon: 'el-icon-keyboard' },
        { name: '图片对比', desc: '左右对照两张图片，支持全屏和滚轮缩放', path: '/image-compare', icon: 'el-icon-picture-outline' }
      ]
    }
  },
  computed: {
    filteredTools() {
      const value = this.keyword.trim().toLowerCase()
      return value ? this.tools.filter(tool => `${tool.name} ${tool.desc}`.toLowerCase().includes(value)) : this.tools
    }
  },
  methods: {
    goTo(tool) {
      if (tool.requiresAuth && !authBus.isLoggedIn) {
        this.$message.warning('使用密码生成器前请先登录')
        authBus.$emit('login-required')
        return
      }
      this.$router.push(tool.path)
    }
  }
}
</script>

<style scoped>
.home-page { padding-bottom: 80px; }

/* Hero 通栏绿色渐变：上下留白对齐设计稿 py-32，渐变在区块 75% 高度处过渡到页面底色 */
.hero {
  padding: 128px 0;
  background: linear-gradient(180deg, var(--brand-soft) 0%, var(--bg) 75%);
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  color: var(--ink-2);
  font-size: 12px;
  font-weight: 500;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 999px;
  box-shadow: var(--shadow-1);
}
.hero-badge i { color: var(--brand); font-size: 14px; }
.hero h1 { margin: 24px 0 0; color: var(--ink); font-size: clamp(36px, 5vw, 56px); font-weight: 700; letter-spacing: -.02em; line-height: 1.1; }
.hero p { margin: 20px 0 0; color: var(--ink-2); font-size: 18px; }
.search-box { position: relative; width: min(600px, 100%); margin-top: 36px; }
.search-box i { position: absolute; top: 50%; left: 16px; color: var(--ink-3); transform: translateY(-50%); }
.search-box input {
  width: 100%;
  height: 48px;
  padding: 0 16px 0 44px;
  font-size: 14px;
  font-family: inherit;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-1);
  outline: none;
  transition: border-color .14s, box-shadow .14s;
}
.search-box input:focus { border-color: var(--brand); box-shadow: 0 0 0 3px rgba(14, 145, 128, .18); }

.section-header { margin: 0 0 32px; }
.section-header h2 { margin: 0; color: var(--ink); font-size: 26px; font-weight: 600; }
.section-header p { margin: 8px 0 0; color: var(--ink-2); font-size: 14px; }
.tool-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 24px; }
.tool-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  cursor: pointer;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-1);
  transition: transform .2s, border-color .2s, box-shadow .2s;
}
.tool-card:hover { transform: translateY(-2px); border-color: rgba(14, 145, 128, .4); box-shadow: var(--shadow-2); }
.tool-icon {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: var(--brand);
  font-size: 22px;
  background: var(--surface-2);
  border-radius: var(--r-md);
}
.tool-info { min-width: 0; flex: 1; }
.tool-info h3 { margin: 0 0 6px; color: var(--ink); font-size: 17px; font-weight: 600; }
.tool-info p { margin: 0; color: var(--ink-2); font-size: 13px; line-height: 1.6; }
.tool-arrow { flex: none; color: var(--ink-3); font-size: 14px; line-height: 44px; transition: color .2s; }
.tool-card:hover .tool-arrow { color: var(--brand); }
.more { background: transparent; border-style: dashed; box-shadow: none; }
.more-icon { color: var(--ink-3); }
.more .tool-info h3 { color: var(--ink-2); }
.more .tool-info p { color: var(--ink-3); }
@media (max-width: 600px) {
  .hero { padding: 56px 0 64px; }
  .tool-grid { grid-template-columns: 1fr; }
}
</style>
